import { createAsyncThunk } from '@reduxjs/toolkit';
import { generatePath } from 'react-router';
import qs from 'qs';
import { checkPermission } from '@shared/lib/checkPermissions';
import { ALL_PRIVATE_ROUTES, ALL_ROUTES } from '@app/router';
import { QUERY_DELAY } from '../../../_legacy/components/Shared/constants';
import { SNACKBAR_VARIANTS } from '../../constants';
import navigationService from '../../services/navigationService';
import {
  enqueueSnackbar,
  setIsNavigating,
  setNavigationError,
  updateBreadcrumbs,
} from '../../slices';

/**
 * Main navigation thunk that handles route changes with permission checks
 * Replaces the setAppRoute function from AppRouteProvider
 * No need to pass navigate - it uses navigationService
 */
export const navigateToRoute = createAsyncThunk(
  'navigation/navigateToRoute',
  async ({ route, options = {} }, { dispatch, getState }) => {
    const navigate = navigationService.getNavigate();
    try {
      dispatch(setIsNavigating(true));

      const state = getState();
      const { metadata, isDistributionSponsor } = state.user;
      const { disabledFeatures } = state.appSettings;
      const { i18n } = state;

      // Update breadcrumbs
      dispatch(updateBreadcrumbs({ route, back: options.back }));

      // Find route configuration
      const parsedRoute = ALL_PRIVATE_ROUTES.find(i => i.path === route.path);

      // Check permissions
      const hasPermission = checkPermission(metadata?.currentAccountPermissions, parsedRoute?.additional?.permission);
      const isNotDisabled = !disabledFeatures.includes(parsedRoute?.additional?.disabledId);
      const isAllowed = hasPermission && isNotDisabled;

      if (!isAllowed) {
        dispatch(
          enqueueSnackbar({
            message: i18n.en.notifications.noPermissions,
            options: {
              variant: SNACKBAR_VARIANTS.warning,
            },
          })
        );

        // Redirect to root
        const rootRoute = {
          path: ALL_ROUTES.root.path,
          params: { currentAccountUuid: metadata?.currentAccount?.uuid },
        };

        dispatch(updateBreadcrumbs({ route: rootRoute, back: false }));
        const rootPath = generatePath(rootRoute.path, rootRoute.params);
        await navigate(rootPath);

        return { success: false, reason: 'NO_PERMISSION' };
      }

      // Generate path
      const path = generatePath(route.path, route.params);

      // Get updated breadcrumbs from state
      const updatedState = getState();
      const breadcrumbs = updatedState.navigation.breadcrumbs;

      // Generate search params
      const search =
        !route.removeQueryString &&
        qs.stringify({
          bc: btoa(unescape(encodeURIComponent(JSON.stringify(breadcrumbs)))),
        });

      // Navigate
      const navigationOptions = {
        search: search,
        replace: route?.replace,
        state: {
          delay: options.addDelay ? QUERY_DELAY : 0,
        },
      };

      await navigate(path, navigationOptions);

      return { success: true, path, breadcrumbs };
    } catch (error) {
      dispatch(setNavigationError(error.message));
      return { success: false, error: error.message };
    } finally {
      dispatch(setIsNavigating(false));
    }
  }
);

/**
 * Navigate back in breadcrumbs
 */
export const navigateBack = createAsyncThunk(
  'navigation/navigateBack',
  async (_, { dispatch, getState }) => {
    const state = getState();
    const { breadcrumbs } = state.navigation;

    if (breadcrumbs.length > 1) {
      // Navigate to the second-to-last breadcrumb
      const previousRoute = breadcrumbs[breadcrumbs.length - 2];
      return dispatch(
        navigateToRoute({
          route: previousRoute,
          options: { back: true }
        })
      );
    }

    // If no breadcrumbs, go to root
    const { metadata } = state.user;
    const rootRoute = {
      path: ALL_ROUTES.root.path,
      params: { currentAccountUuid: metadata?.currentAccount?.uuid },
      clearBreadcrumbs: true,
    };

    return dispatch(
      navigateToRoute({
        route: rootRoute,
      })
    );
  }
);

/**
 * Navigate to a specific breadcrumb
 */
export const navigateToBreadcrumb = createAsyncThunk(
  'navigation/navigateToBreadcrumb',
  async ({ index }, { dispatch, getState }) => {
    const state = getState();
    const { breadcrumbs } = state.navigation;

    if (index >= 0 && index < breadcrumbs.length) {
      const targetRoute = breadcrumbs[index];
      return dispatch(
        navigateToRoute({
          route: targetRoute,
          options: { back: index < breadcrumbs.length - 1 }
        })
      );
    }

    throw new Error('Invalid breadcrumb index');
  }
);

/**
 * Clear all breadcrumbs and navigate to root
 */
export const navigateToRoot = createAsyncThunk(
  'navigation/navigateToRoot',
  async (_, { dispatch, getState }) => {
    const state = getState();
    const { metadata } = state.user;

    const rootRoute = {
      path: ALL_ROUTES.root.path,
      params: { currentAccountUuid: metadata?.currentAccount?.uuid },
      clearBreadcrumbs: true,
    };

    return dispatch(
      navigateToRoute({
        route: rootRoute,
      })
    );
  }
);

/**
 * Helper thunk for permission-based navigation
 */
export const navigateWithPermission = createAsyncThunk(
  'navigation/navigateWithPermission',
  async ({ route, permission }, { dispatch, getState }) => {
    const state = getState();
    const { metadata } = state.user;

    const hasPermission = checkPermission(metadata?.currentAccountPermissions, permission);

    if (!hasPermission) {
      const { i18n } = state;
      dispatch(
        enqueueSnackbar({
          message: i18n.en.notifications.noPermissions,
          options: {
            variant: SNACKBAR_VARIANTS.warning,
          },
        })
      );
      return { success: false, reason: 'NO_PERMISSION' };
    }

    return dispatch(navigateToRoute({ route }));
  }
);
